import { ApolloClient, ApolloLink, HttpLink, InMemoryCache, Observable } from '@apollo/client';
import { fetchAuthSession } from 'aws-amplify/auth';
import fetch from 'cross-fetch';

// Cognito hands out a short-lived (~1h) idToken. fetchAuthSession() is meant to
// refresh it transparently, but during the rehydrate window after the tab has been
// idle it can return an already-expired (or absent) idToken. Firing a request with
// that token yields a 401, which callers can misread as a legitimate empty response
// (e.g. the Terms-of-Service gate treating it as "not yet accepted"). Force a refresh
// whenever the idToken is missing or within the skew window of expiry so authenticated
// requests always go out with a live token.
const TOKEN_EXPIRY_SKEW_MS = 30_000;

export const getFreshIdToken = async () => {
  const session = await fetchAuthSession();
  const idToken = session?.tokens?.idToken;
  const expiresAtMs = (idToken?.payload?.exp ?? 0) * 1000;
  const isExpired = expiresAtMs - TOKEN_EXPIRY_SKEW_MS <= Date.now();

  if (idToken && !isExpired) {
    return idToken;
  }

  const refreshed = await fetchAuthSession({ forceRefresh: true });
  return refreshed?.tokens?.idToken;
};

const authLink = new ApolloLink((operation, forward) => {
  return new Observable((observer) => {
    getFreshIdToken()
      .then((token) => {
        // Skip the header entirely when there is no token rather than sending
        // `Bearer undefined`, which the API rejects as an opaque 401.
        if (token) {
          operation.setContext(({ headers = {} }) => ({
            headers: {
              ...headers,
              Authorization: `Bearer ${token}`
            }
          }));
        }

        const subscriber = {
          next: observer.next.bind(observer),
          error: observer.error.bind(observer),
          complete: observer.complete.bind(observer)
        };

        forward(operation).subscribe(subscriber);
      })
      .catch((error) => {
        observer.error(error);
      });
  });
});

const createLink = ({ uri }: { uri: string }) => {
  return authLink.concat(new HttpLink({ uri, fetch }));
};

export const createGqlClient = ({ uri }: { uri: string }) =>
  new ApolloClient({
    link: createLink({ uri }),
    cache: new InMemoryCache()
  });
